import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Search, Database } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useKnowledgeBases, KnowledgeBase } from '@/hooks/useKnowledgeBases'

interface Step2SelectKBsProps {
  selectedKBIds: string[]
  onSelectionChange: (ids: string[]) => void
  errors: {
    knowledgeBases?: string
  }
}

export function Step2SelectKBs({
  selectedKBIds,
  onSelectionChange,
  errors,
}: Step2SelectKBsProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const { data: kbData, isLoading } = useKnowledgeBases({ page: 1, pageSize: 100, search: '' })
  const knowledgeBases = kbData?.items

  const filteredKBs = useMemo(() => {
    if (!knowledgeBases) return []
    if (!searchQuery.trim()) return knowledgeBases

    const query = searchQuery.toLowerCase()
    return knowledgeBases.filter((kb) =>
      kb.name.toLowerCase().includes(query)
    )
  }, [knowledgeBases, searchQuery])

  const handleToggle = (kbId: string) => {
    if (selectedKBIds.includes(kbId)) {
      onSelectionChange(selectedKBIds.filter((id) => id !== kbId))
    } else {
      onSelectionChange([...selectedKBIds, kbId])
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-muted-foreground">Loading knowledge bases...</div>
      </div>
    )
  }

  // Empty state - no KBs exist
  if (!knowledgeBases || (knowledgeBases as any[]).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Database className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No knowledge bases found</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Create a knowledge base first to continue
        </p>
        <Button asChild>
          <Link to="/app/knowledge">Go to Knowledge Bases</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search knowledge bases..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Selection Count & Error */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">
          {selectedKBIds.length} knowledge base{selectedKBIds.length !== 1 ? 's' : ''} selected
        </span>
        {errors.knowledgeBases && (
          <span className="text-sm text-destructive">{errors.knowledgeBases}</span>
        )}
      </div>

      {/* KB List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filteredKBs.map((kb) => (
          <KBListItem
            key={kb.id}
            kb={kb}
            isSelected={selectedKBIds.includes(kb.id)}
            onToggle={() => handleToggle(kb.id)}
          />
        ))}

        {filteredKBs.length === 0 && searchQuery && (
          <div className="text-center py-8 text-muted-foreground">
            No knowledge bases match your search
          </div>
        )}
      </div>
    </div>
  )
}

interface KBListItemProps {
  kb: KnowledgeBase
  isSelected: boolean
  onToggle: () => void
}

function KBListItem({ kb, isSelected, onToggle }: KBListItemProps) {
  return (
    <div
      onClick={onToggle}
      className={`
        flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors
        ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/50'}
      `}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{kb.name}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {kb.document_count} doc{kb.document_count !== 1 ? 's' : ''} •{' '}
          Last updated {formatDistanceToNow(new Date(kb.updated_at), { addSuffix: false })} ago
        </div>
      </div>
    </div>
  )
}
